import AHDate from '_shared/dates/date';
import AddCommentIcon from '@mui/icons-material/AddCommentOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssistantIcon from "@mui/icons-material/Assistant";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import SendIcon from "@mui/icons-material/Send";
import StopCircleIcon from "@mui/icons-material/StopCircleOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { alpha, styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { ConfirmResponse, useConfirmDialog } from '_shared/_providers/dialogs/confirm/confirm-dialog';
import { KeyboardKey } from '_shared/_utilities/keyboard-keys';
import {
  useCancelStagedActionMutation,
  useConfirmStagedActionMutation,
  useDeleteAllAssistantConversationsMutation,
  useDeleteAssistantConversationMutation,
  useLazyReadAssistantConversationQuery,
  useListAssistantConversationsQuery,
  useSendAssistantMessageMutation
} from 'ai-assistant/_api/ai-assistant-api-service';
import { MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS } from 'ai-assistant/_api/commands/assistant-api-command';
import { ASSISTANT_PANEL_ID, useAssistantDock } from 'ai-assistant/dock/assistant-dock';
import AssistantConversation from 'ai-assistant/conversations/assistant-conversation';
import AssistantConversationList from 'ai-assistant/conversations/_views/assistant-conversation-list';
import AssistantConversationSummary from 'ai-assistant/conversations/assistant-conversation-summary';
import {
  describeConversationDeleteFailure,
  describeConversationReadFailure
} from 'ai-assistant/conversations/assistant-conversation-failure';
import { describeTurnFailure } from 'ai-assistant/conversations/assistant-turn-failure';
import AssistantResponse from 'ai-assistant/assistant-response';
import AttachFilesButton from 'ai-assistant/attachments/_views/attach-files-button';
import AssistantAttachmentStrip from 'ai-assistant/attachments/_views/assistant-attachment-strip';
import SentAttachmentList from 'ai-assistant/attachments/_views/sent-attachment-list';
import { useAssistantAttachments } from 'ai-assistant/attachments/_views/use-assistant-attachments';
import { turnAttachmentRefusal } from 'ai-assistant/attachments/assistant-attachment-failure';
import CitedAnswer from 'ai-assistant/citations/_views/cited-answer';
import StagedActionCard from 'ai-assistant/staged-actions/_views/staged-action-card';
import ResultTableCard from 'ai-assistant/result-tables/_views/result-table-card';
import AnswerFeedbackControl from 'ai-assistant/feedback/_views/answer-feedback-control';
import TurnStepsSummary from 'ai-assistant/turn-steps/_views/turn-steps-summary';
import { useLiveTurnStep } from 'ai-assistant/turn-steps/_views/use-live-turn-step';
import Guid from '_shared/guids/guid';
import {
  describeResolutionFailure,
  StagedActionIntent,
  StagedActionResolutionFailure
} from 'ai-assistant/staged-actions/staged-action-resolution-failure';
import React from "react";
import { useLocation } from 'react-router';
import { useReadAssistantScreenContext } from 'ai-assistant/screen-context/assistant-screen-context-provider';
import { useSession } from 'users/__session/session-context';
import { Role } from 'ai-assistant/roles/assistant-role';

/** What each card has been told about an answer that never arrived, keyed by action id. */
type ResolutionFailures = Readonly<Record<string, StagedActionResolutionFailure>>;

/**
 * Whether a rejected turn was stopped by the user rather than broken.
 *
 * RTK Query rejects an aborted request with an `AbortError`, which is the only signal there
 * is: the request never reached a response, so there is no status to read. Module scope
 * because the distinction is worth stating once - everything else about the two cases is
 * identical, and treating a deliberate stop as an error puts it in the console and tells the
 * user something went wrong when nothing did.
 */
function wasStoppedByUser(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'name' in error
    && (error as { name: unknown }).name === 'AbortError';
}

/**
 * What the panel says out loud about a turn, for a reader who cannot see it arrive.
 *
 * One sentence at a time, rendered into a single polite live region. A screen reader speaks a
 * live region when its content *changes*, so this returns a value derived from state rather
 * than pushing announcements: "working" and then the answer are two different strings, so the
 * arrival is spoken without anything having to remember that it fired.
 *
 * A finished answer is announced with its text. The alternative - "the assistant answered" and
 * nothing more - leaves a blind user to go hunting through the transcript for the reply to a
 * question they just asked, every single turn. It also keeps consecutive turns distinguishable:
 * two identical sentences in a row are one silent announcement.
 *
 * A turn that staged an action is the exception, and is announced without its text. Focus moves
 * to the prepared action's card in that case (see `decisionToFocus`), and the reader speaks a
 * focus change ahead of a polite region - so reading the answer here would either be interrupted
 * or queued behind the thing it is meant to introduce.
 *
 * Two kinds of turn are deliberately silent here, because they already speak for themselves: a
 * failure renders as `role="alert"`, and a stopped turn as a `role="status"` note.
 */
function announcementFor(messages: AssistantResponse[], showResponding: boolean): string {
  if (showResponding) return 'The assistant is working on your answer.';

  const latest = messages[messages.length - 1];
  if (latest === undefined) return '';
  if (latest.role === Role.User) return '';
  if (!latest.showsAnswerText) return '';

  if (latest.actionableStagedActions.some(action => action.isPending)) {
    return 'The assistant answered, and prepared an action for you to approve.';
  }

  return `The assistant answered. ${latest.text}`;
}

/** The prepared action a turn has just put in front of the user, if it put one there. */
function decisionStagedBy(reply: AssistantResponse): string | undefined {
  return reply.actionableStagedActions.find(action => action.isPending)?.id.value;
}

/**
 * Confirms or cancels a prepared action and appends the assistant's reply.
 *
 * Module scope on purpose. `app/CLAUDE.md` bans `try/finally` inside a component because the
 * React Compiler cannot lower one and silently stops memoizing the whole component - and the
 * component this belongs to already carries that scar in `handleSendMessage`. Module-level
 * functions are not compiled, so the control flow here survives exactly as written.
 *
 * A refusal is an answer, not an error: the server replies 200 with prose explaining that the
 * action expired or was already handled, and that reply is appended like any other. What reaches
 * the catch is a request that produced no answer at all, and the card it was sent from has to
 * say so - `console.error` alone left a 403, a dropped connection and a timeout all rendering
 * as nothing having happened.
 */
async function resolveStagedAction(
  resolve: (request: { stagedActionId: string }) => { unwrap: () => Promise<AssistantResponse> },
  stagedActionId: string,
  intent: StagedActionIntent,
  setMessages: React.Dispatch<React.SetStateAction<AssistantResponse[]>>,
  setResolvingActionIds: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>,
  setResolutionFailures: React.Dispatch<React.SetStateAction<ResolutionFailures>>
): Promise<void> {
  try {
    const reply = await resolve({ stagedActionId }).unwrap();
    setMessages(previous => appendResolution(previous, reply));
  } catch (error) {
    console.error('Error resolving a staged assistant action:', error);
    setResolutionFailures(previous => ({
      ...previous,
      [stagedActionId]: describeResolutionFailure(error, intent),
    }));
  } finally {
    // This id only. Two resolutions really can be in flight at once, so a set rather than a
    // slot: one request finishing must not speak for another, and one starting must not clear
    // another's spinner and re-enable a Confirm whose own request is still running.
    setResolvingActionIds(previous => withoutResolvingAction(previous, stagedActionId));
  }
}

/**
 * Loads a conversation the server already holds into the panel.
 *
 * The transcript used to exist only in this component's state, so a refresh lost it while the
 * server kept every turn indefinitely - a user could see none of their own assistant history and
 * delete none of it either. See #4658.
 *
 * Module scope for the reason `resolveStagedAction` is: `app/CLAUDE.md` bans `try/finally` and a
 * `throw` inside a `try` in a component because the React Compiler cannot lower either and
 * silently stops memoizing the whole component.
 *
 * A failure here is not worth surfacing. The user opened a panel; the worst case is that they
 * start a new conversation instead of continuing the last one, which is what happened before this
 * existed.
 */
async function hydrateConversation(
  conversationId: string,
  read: (request: { conversationId: string }) => { unwrap: () => Promise<AssistantConversation> },
  isCurrent: () => boolean,
  setMessages: React.Dispatch<React.SetStateAction<AssistantResponse[]>>,
  setConversationId: React.Dispatch<React.SetStateAction<string | undefined>>
): Promise<void> {
  try {
    const conversation = await read({ conversationId }).unwrap();
    // Not into a panel the user has since emptied on purpose, or filled with another
    // conversation. "Start a new conversation" leaves the panel looking exactly like one nobody
    // has used, so the check below cannot tell the two apart; the transcript's generation can.
    if (!isCurrent()) return;
    // Only into a panel nobody has used yet. This read is asynchronous and the composer works
    // throughout it, so somebody who opens the assistant and types straight away has a turn on
    // screen before the answer arrives. Replacing the transcript then erased the question they
    // had just asked and whatever came back with it — a prepared action among it — and
    // left them reading a conversation they never opened, under a banner calling it history.
    // A panel with anything in it is a conversation of its own, and history stays where it is.
    setMessages(previous => (previous.length > 0 ? previous : conversation.toTranscript()));
    setConversationId(current => current ?? conversation.conversationId);
  } catch (error) {
    console.error('Error loading an assistant conversation:', error);
  }
}

/**
 * Opens a conversation the user chose from their history, in place of whatever was on screen.
 *
 * Unlike `hydrateConversation`, which only fills an empty panel, this replaces the transcript:
 * the user asked for this conversation by name. A read that fails leaves the panel as it was and
 * says why, rather than opening an empty conversation that would continue nothing.
 *
 * `isCurrent` is how a slow read loses to a later choice. A user who clicks one conversation and
 * then another has asked for the second, whichever read answers first.
 */
async function openEarlierConversation(
  conversationId: string,
  read: (request: { conversationId: string }) => { unwrap: () => Promise<AssistantConversation> },
  isCurrent: () => boolean,
  show: (conversation: AssistantConversation) => void,
  fail: (message: string) => void
): Promise<void> {
  try {
    const conversation = await read({ conversationId }).unwrap();
    if (isCurrent()) show(conversation);
  } catch (error) {
    console.error('Error opening an assistant conversation:', error);
    if (isCurrent()) fail(describeConversationReadFailure(error));
  }
}

/**
 * Erases one conversation. A legal hold keeps it, and the user is told so rather than that it
 * failed.
 */
async function deleteEarlierConversation(
  conversationId: string,
  erase: (request: { conversationId: string }) => { unwrap: () => Promise<void> },
  deleted: () => void,
  fail: (message: string) => void
): Promise<void> {
  try {
    await erase({ conversationId }).unwrap();
    deleted();
  } catch (error) {
    console.error('Error deleting an assistant conversation:', error);
    fail(describeConversationDeleteFailure(error));
  }
}

/**
 * Erases the user's assistant history, on the server and on screen.
 *
 * Conversations under legal hold survive, and the user is told when any did rather than being
 * shown an empty panel that implies everything is gone.
 */
async function clearAssistantHistory(
  erase: () => { unwrap: () => Promise<{ deleted: number; retainedUnderLegalHold: number }> },
  cleared: () => void,
  setNotice: React.Dispatch<React.SetStateAction<string | undefined>>
): Promise<void> {
  try {
    const erasure = await erase().unwrap();
    cleared();
    setNotice(
      erasure.retainedUnderLegalHold > 0
        ? `Deleted ${erasure.deleted} conversation(s). `
          + `${erasure.retainedUnderLegalHold} are preserved and could not be deleted.`
        : undefined
    );
  } catch (error) {
    console.error('Error deleting assistant conversations:', error);
    setNotice('Your conversations could not be deleted. Please try again.');
  }
}

/**
 * Folds a resolution into the transcript.
 *
 * The server answers a confirm or a cancel with the prepared action in the state it moved it
 * to, so the card the user clicked is redrawn where it already sits and the copy travelling
 * with the reply is dropped. Appending the reply as it arrived would leave the original card
 * still offering Confirm and Cancel for work that is already done, and would show two cards
 * for one action id.
 */
function appendResolution(
  messages: AssistantResponse[],
  reply: AssistantResponse
): AssistantResponse[] {
  let transcript = messages;
  for (const resolved of reply.stagedActions) {
    transcript = transcript.map(message => message.withStagedActionResolved(resolved));
  }

  return [...transcript, reply.withoutStagedActions()];
}

/**
 * Appends a turn, retiring any preview the user left unanswered.
 *
 * Staging a new action supersedes the outstanding ones in that conversation on the server, in
 * the same transaction. The reply says nothing about it - a turn carries only what it staged -
 * so the transcript has to apply the same rule, or a card from three messages ago goes on
 * offering a Confirm whose only remaining answer is a refusal. A turn that stages nothing
 * retires nothing: an unanswered preview outlives an ordinary reply.
 *
 * `confirmed` is every action this panel has sent a confirmation for, and none of them is
 * retired here whatever the transcript still says about them. The server does not supersede an
 * action it is executing, and a card whose confirmation came back uncertain is the one card in
 * the panel that must go on saying so - relabelling it "something newer replaced this" would
 * withdraw an honest "I cannot tell whether that went through" about a write that may have
 * happened. Their state is the server's to report, in the reply to the confirmation they were
 * sent.
 *
 * A cancellation is deliberately not in that set. A cancel writes no business data, so a cancel
 * whose request never arrived - the one failure the panel offers again - leaves the action in
 * whatever state the server has it in, and once a newer preview exists that state is superseded.
 * Retaining it there would leave a card still offering Confirm and Cancel for an action the
 * server has already retired, which is the stale card this function exists to prevent.
 */
function appendTurn(
  messages: AssistantResponse[],
  reply: AssistantResponse,
  confirmed: ReadonlySet<string>
): AssistantResponse[] {
  if (reply.stagedActions.length === 0) return [...messages, reply];

  return [
    ...messages.map(message => message.withPendingStagedActionsSuperseded(confirmed)),
    reply,
  ];
}

/** The in-flight set without one id, and the same set when it was never in it. */
function withoutResolvingAction(
  resolving: ReadonlySet<string>,
  stagedActionId: string
): ReadonlySet<string> {
  if (!resolving.has(stagedActionId)) return resolving;

  const remaining = new Set(resolving);
  remaining.delete(stagedActionId);
  return remaining;
}

/** A card's previous failure, cleared because it is being answered again. */
function withoutResolutionFailure(
  failures: ResolutionFailures,
  stagedActionId: string
): ResolutionFailures {
  if (failures[stagedActionId] === undefined) return failures;

  const remaining = { ...failures };
  delete remaining[stagedActionId];
  return remaining;
}

// #region Styled Components
/**
 * The panel, docked: it fills the column the shell gives it (#6128), rather than floating at a
 * size of its own over the page.
 */
const PanelContainer = styled('aside')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  height: '100%',
  minHeight: 0,
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
}));
const Header = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.primary.main,
  position: 'relative',
  color: theme.palette.common.white,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: theme.spacing(1),
  'h3': {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    fontWeight: 500,
  }
}));
const Content = styled('div')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  paddingRight: theme.spacing(1),
  overflowX: 'hidden',
  overflowY: 'scroll',
  scrollbarGutter: 'stable',
}));
const MessageEditor = styled(TextField)(() => ({
  backgroundColor: '#f5f5f5',
  borderRadius: 0,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
const Message = styled('div')<{ sent: boolean }>(
  ({ theme, sent }) => ({
    containerType: 'inline-size',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: sent ? 'row-reverse' : 'row',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& .message-text': {
      backgroundColor: sent ? '#bdf' : '#f0f0f0',
      '@container (min-width: 0px)': {
        maxWidth: '90%'
      },
      '@container (min-width: 600px)': {
        maxWidth: '75%'
      },
      '@container (min-width: 800px)': {
        maxWidth: '66%'
      }
    },
  }));
const MessageIcon = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  marginTop: theme.spacing(1),
}));
const MessageText = styled('div')(({ theme }) => ({
  backgroundColor: '#f0f0f0',
  borderRadius: '8px',
  color: theme.palette.text.primary,
  fontSize: '0.9rem',
  lineHeight: '1.5',
  marginBottom: theme.spacing(2),
  wordBreak: 'break-word',
  padding: theme.spacing(1.5, 2),
  "& p": {
    margin: 0,
  }
}));
/**
 * A turn the assistant could not answer.
 *
 * Every reply is HTTP 200 carrying prose, and a failed one carries no citations - which is
 * exactly what a correct abstention looks like. Without a visible difference, "an error
 * occurred" reads as an answer, and the one action that helps (send it again) looks like
 * arguing with the assistant.
 */
const FailedMessageText = styled(MessageText)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.contrastText,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));
/**
 * The caveat on a turn the assistant did not finish.
 *
 * A warning beside the text rather than the error styling a failed turn gets: the text is
 * real and worth reading, it is just not all of the answer. Styled as a caveat so it is not
 * mistaken for an error, and marked `role="status"` so a screen reader announces it with the
 * message instead of interrupting as an alert would.
 */
const IncompleteNotice = styled('div')(({ theme }) => ({
  alignItems: 'flex-start',
  backgroundColor: theme.palette.warning.light,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.warning.contrastText,
  display: 'flex',
  fontSize: '0.75rem',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
}));
/**
 * Said where a replayed transcript's prepared work used to be offered.
 *
 * Quieter than `IncompleteNotice` on purpose - nothing has gone wrong, and the reader is being
 * told why a control the answer above refers to is not there rather than warned about anything.
 */
const ReplayedActionNotice = styled('div')(({ theme }) => ({
  alignItems: 'flex-start',
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  display: 'flex',
  fontSize: '0.75rem',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
}));
/**
 * Holds the turning ring and the Stop button, in that order and side by side.
 *
 * They were concentric, the ring turning behind the button. Two round things sharing a centre
 * read as one thickened control rather than as a state and a thing to press, and the ring's
 * stroke crossed the icon's own outline. Sat next to each other they are legible as two.
 */
const SendingControls = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: theme.spacing(0.5),
}));
/** The composer's controls while nothing is in flight: attach and send, side by side. */
const ComposerControls = SendingControls;
const SendingSpinner = styled(CircularProgress)({
  // Out of the button's way: a spinner that swallowed the click would be the same defect as
  // the disabled button it replaced.
  pointerEvents: 'none',
});
/**
 * Said where a stopped turn's answer would have been.
 *
 * Not a message bubble. The assistant did not say this and must not appear to have - it is
 * the panel reporting what the user just did, which is why it reads as a note and not as a turn.
 */
const StoppedNotice = styled('div')(({ theme }) => ({
  alignItems: 'center',
  alignSelf: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  fontSize: '0.75rem',
  gap: theme.spacing(0.5),
  margin: theme.spacing(1, 0),
}));
/**
 * The panel's one live region, heard and never seen.
 *
 * Mounted for as long as the panel is open and empty most of that time, because a live region
 * has to be in the document *before* its content changes for the change to be spoken. Inserting
 * a new element that carries `aria-live` announces nothing reliably, which is why the animated
 * "Responding..." bubble and the answer bubbles cannot do this job themselves.
 *
 * `role="log"` rather than `status`: this is the running record of a conversation, and the panel
 * already uses `status` for standing notices - a second one would make `getByRole('status')`
 * ambiguous for anything reading the panel, tests included.
 *
 * Hidden the long way round rather than with `display: none` or `visibility: hidden`, either of
 * which takes the element out of the accessibility tree and silences it.
 */
const LiveAnnouncement = styled('div')({
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
});
const Timestamp = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.6rem',
  marginTop: theme.spacing(0.5),
  width: '100%'
}));
const MarkdownStyles = styled('div')(({ theme }) => ({
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& code': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    padding: '2px 4px',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  '& pre': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    padding: '10px',
    overflowX: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: '10px',
    color: theme.palette.text.secondary,
    margin: '10px 0',
  },
  '& ul, & ol': {
    paddingLeft: '20px',
    margin: '10px 0',
    '& li': {
      marginBottom: '5px',
    },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '10px 0',
    fontWeight: 'bold',
  },
  '& strong': {
    fontWeight: 'bold',
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: '20px 0',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '4px',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: theme.spacing(1),
    '& th, & td': {
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      fontSize: '0.9em',
      padding: '8px',
      textAlign: 'left',
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      whiteSpace: 'normal',
    },
    '& th': {
      backgroundColor: theme.palette.background.default,
      fontSize: '0.8em',
      fontWeight: 'bold',
    },
  },
}));
/**
 * The controls in the panel's title bar.
 *
 * One positioned box holding a row, rather than each button positioning itself. Both buttons
 * used to be the same styled component, each `position: absolute; right: 8px` - so they were
 * laid on top of one another and the pair read as a single smudged glyph. The title stays
 * centered because this box is out of the flex flow, which is what the absolute positioning
 * was for.
 */
const HeaderActions = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(0.5),
  position: 'absolute',
  right: theme.spacing(1),
}));
const HeaderLeadingActions = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  left: theme.spacing(1),
  position: 'absolute',
}));
const HeaderIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  '&.Mui-disabled': {
    color: alpha(theme.palette.common.white, 0.5),
  },
}));
// #endregion Styled Components

const AiAssistantSession = React.memo(
  function AiAssistantSession() {
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const messageEditorInputRef = React.useRef<HTMLInputElement>(null);
    /**
     * The prepared action's card, when a turn has just put a decision in front of the user.
     *
     * Held by the parent rather than focused by the card on mount, because the card mounts
     * again every time the panel is reopened - the panel unmounts when it closes -
     * and landing on last week's unanswered preview is not what "focus the new decision" means.
     */
    const decisionCardRef = React.useRef<HTMLDivElement>(null);
    /**
     * Names the panel for assistive technology, which is what makes `role="dialog"` worth
     * having: an unnamed dialog is announced as "dialog" and nothing else.
     */
    const panelTitleId = React.useId();

    const dock = useAssistantDock();
    const isOpen = dock.isOpen;
    const [inputText, setInputText] = React.useState<string>('');
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const [respondingDelayElapsed, setRespondingDelayElapsed] = React.useState<boolean>(false);
    const [messages, setMessages] = React.useState<AssistantResponse[]>([]);
    const [conversationId, setConversationId] = React.useState<string | undefined>();
    const [dotCount, setDotCount] = React.useState<number>(1);
    /**
     * The id this panel gave the turn in flight, so the server's live steps for it can be told
     * from any other turn's (#7286). The reply is still the only source of the answer: a step
     * that never arrives leaves "Responding...", and nothing else.
     */
    const [liveTurnId, setLiveTurnId] = React.useState<string | undefined>();
    const liveStep = useLiveTurnStep(liveTurnId);
    /**
     * Whether this panel has already tried to load the user's last conversation.
     *
     * Tried, not succeeded. A second attempt would re-fetch a conversation the user may have
     * deliberately cleared, and would fight whatever they have typed since.
     *
     * A ref rather than state: nothing renders differently for knowing it, and setting state
     * inside the effect that reads it is a cascading render the `react-hooks/set-state-in-effect`
     * rule rightly objects to.
     */
    const historyHydratedRef = React.useRef<boolean>(false);
    /** What the last open or delete in the conversation list said, when it did not simply work. */
    const [historyNotice, setHistoryNotice] = React.useState<string | undefined>();
    /** Whether the panel shows the user's conversations in place of the transcript. */
    const [showingHistory, setShowingHistory] = React.useState<boolean>(false);
    /** Whether an open or a delete from the list is under way, which disables the list. */
    const [historyBusy, setHistoryBusy] = React.useState<boolean>(false);
    /**
     * Counts the times the transcript was replaced - a new conversation, an earlier one opened,
     * one deleted while open.
     *
     * Anything asynchronous that would write into the transcript notes the count when it starts
     * and writes only if it has not moved, so a read or a reply that lands late cannot put one
     * conversation's turns into another. The list's controls are also off while a turn is in
     * flight; this is what still holds for the reads that controls cannot stop, such as the
     * one that picks up the last conversation when the panel opens.
     *
     * A ref rather than state: nothing renders from it.
     */
    const transcriptGenerationRef = React.useRef<number>(0);
    /**
     * The turn in flight, kept only so it can be stopped.
     *
     * A ref rather than state: nothing renders differently for holding it - `isSending` is what
     * the Stop control keys off - and putting a mutation handle in state would re-render the
     * panel for a value nothing reads during render.
     */
    const pendingTurnRef = React.useRef<{ abort: () => void } | undefined>(undefined);
    const [stoppedNotice, setStoppedNotice] = React.useState<string | undefined>();
    // A message that got no answer at all: refused by a limit, or failed before an answer. Shown
    // where the answer would have been, as the stopped notice is, rather than only in the console.
    const [turnFailure, setTurnFailure] = React.useState<string | undefined>();
    /**
     * The prepared action the last turn staged, until something answers it or the panel closes.
     *
     * State rather than a ref because the render reads it - it decides which card gets
     * `decisionCardRef` - and it is set from an event handler, never from an effect.
     */
    const [decisionToFocus, setDecisionToFocus] = React.useState<string | undefined>();
    /**
     * Whether the panel has been opened, so that closing it can put focus back where it came from
     * without a first render - where nothing has been opened and nothing was taken - stealing
     * focus from whatever the page was doing.
     */
    const hasBeenOpenedRef = React.useRef<boolean>(false);

    const trimmedInputLength = inputText.trim().length;
    const isMessageEmpty: boolean = trimmedInputLength === 0;
    const isMessageTooLong = trimmedInputLength > MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS;
    const messageValidationError = isMessageTooLong
      ? `Messages must be ${MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS.toLocaleString()} characters or fewer.`
      : undefined;
    // The files going with the next message (#6504). A message still needs words: the server
    // requires them, and a file with no question says nothing about what to do with it.
    const attachments = useAssistantAttachments();
    const canSendMessage = !isMessageEmpty && !isMessageTooLong && attachments.sendBlockedReason === undefined;
    const showResponding = isSending && respondingDelayElapsed;
    const announcement = announcementFor(messages, showResponding);

    const session = useSession();
    const location = useLocation();
    const readScreenContext = useReadAssistantScreenContext();
    const [sendAssistantMessage] = useSendAssistantMessageMutation();
    const [confirmStagedAction] = useConfirmStagedActionMutation();
    const [cancelStagedAction] = useCancelStagedActionMutation();
    // Skipped until the panel is open. A panel nobody has opened should not be reading a
    // user's assistant history.
    const { data: conversationSummaries, isError: conversationListFailed } = useListAssistantConversationsQuery(
      undefined,
      { skip: !isOpen }
    );
    const [readConversation] = useLazyReadAssistantConversationQuery();
    const [deleteConversation] = useDeleteAssistantConversationMutation();
    const [deleteAllConversations] = useDeleteAllAssistantConversationsMutation();
    const confirm = useConfirmDialog();
    const [resolvingActionIds, setResolvingActionIds] = React.useState<ReadonlySet<string>>(
      () => new Set<string>()
    );
    const [resolutionFailures, setResolutionFailures] = React.useState<ResolutionFailures>({});
    /**
     * Every prepared action this panel has sent a *confirmation* for, whatever became of it.
     *
     * A ref rather than state: nothing renders differently for knowing it. It is read when a
     * new turn retires the previews the user left alone, and a confirmed card is not one of
     * those - see `appendTurn`. Ids are never removed, because "I approved that one" does not
     * stop being true.
     *
     * Cancellations are not recorded here. They were, and it retained a card the server had
     * already superseded: a cancel that fails is the one resolution the panel offers again, so
     * a user whose cancel dropped and who then said something else was left with a stale card
     * still offering Confirm for an action nothing could answer any more.
     */
    const confirmedActionIdsRef = React.useRef<Set<string>>(new Set<string>());

    const scrollToBottom = (delayMs: number = 0) => {
      if (!bottomRef.current) return;
      setTimeout(() => {
        bottomRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: "nearest",
          inline: "nearest"
        });
      }, delayMs);
    };

    /**
     * Holds the composer, and puts the caret in it the moment the panel appears.
     *
     * A callback ref rather than an effect keyed on `isOpen`, because the panel renders through
     * a `Portal` and a portal's children are not in the document on the commit that opens it -
     * an effect can therefore run with nothing to focus. This runs when the field itself mounts,
     * which is once per opening, since the panel unmounts when it closes.
     *
     * Stable on purpose: an inline callback would be invoked again on every commit and drag the
     * caret back to the start of whatever the user was typing.
     */
    const handleComposerRef = React.useCallback((node: HTMLInputElement | null) => {
      messageEditorInputRef.current = node;
      node?.focus();
    }, []);

    /** Holds the card of the decision this turn staged, for the effect below to focus. */
    const captureDecisionCard = React.useCallback((element: HTMLDivElement | null) => {
      decisionCardRef.current = element;
    }, []);

    React.useEffect(() => {
      let timeout: ReturnType<typeof setTimeout>;

      if (isSending) {
        timeout = setTimeout(() => {
          setRespondingDelayElapsed(true);
        }, 1000);
      }

      return () => {
        clearTimeout(timeout);
      };
    }, [
      isSending
    ]);

    React.useEffect(() => {
      if (showResponding) {
        const interval = setInterval(() => {
          setDotCount(prev => (prev % 3) + 1); // Cycle through 1, 2, 3
        }, 500); // Change every 500ms

        return () => clearInterval(interval);
      }
    }, [
      showResponding
    ]);

    /**
     * Picks up where the user left off, once, when the panel is first opened.
     *
     * The most recent conversation rather than a picker: the panel is a single thread and
     * continuing the last thread is what "survives a refresh" means here. A list view over
     * `conversationSummaries` is the shape this grows into.
     */
    React.useEffect(() => {
      if (!isOpen || historyHydratedRef.current || conversationSummaries === undefined) return;

      historyHydratedRef.current = true;
      const mostRecent = conversationSummaries[0];
      if (mostRecent === undefined) return;

      const generation = transcriptGenerationRef.current;
      void hydrateConversation(
        mostRecent.conversationId,
        readConversation,
        () => transcriptGenerationRef.current === generation,
        setMessages,
        setConversationId
      );
    }, [
      isOpen,
      conversationSummaries,
      readConversation
    ]);

    /**
     * Where the keyboard lands once a turn has finished arriving.
     *
     * The composer, unless the turn put a decision in front of the user - in which case the
     * prepared action's card, because a write waiting for approval is not something to leave a
     * keyboard user to find by tabbing past the whole transcript. The card and not its Confirm
     * button: landing on the button means one reflexive Enter performs the write before the
     * reader has said what it is.
     *
     * Guarded on `isOpen` because this used to run whether the panel was open or not, and a
     * reply that arrived after the user closed it took the focus off the page.
     *
     * Also run when the conversation list closes. The transcript is drawn afresh then, scrolled
     * to its top, and coming back to it landed the user at the start of the conversation with its
     * latest message under the composer (#5071's review).
     */
    React.useEffect(() => {
      if (!isOpen || showingHistory) return;

      scrollToBottom(100);
      const decision = decisionCardRef.current;
      if (decision !== null) {
        decision.focus();
        return;
      }

      messageEditorInputRef.current?.focus();
    }, [
      isOpen,
      messages,
      showResponding,
      showingHistory
    ]);

    /**
     * Hands the keyboard back when the panel closes.
     *
     * Whatever closed it - Escape, the Close button, the shortcut - focus would otherwise fall to the
     * document body, which is where a keyboard user has to start tabbing from again. It goes back to
     * whatever opened the panel. Where the nav entry opened it, that entry was replaced by the panel
     * and is drawn afresh by the time this runs, so the launcher now on screen stands in for it.
     */
    React.useEffect(() => {
      if (isOpen) {
        hasBeenOpenedRef.current = true;
        return;
      }

      if (!hasBeenOpenedRef.current) return;
      hasBeenOpenedRef.current = false;
      const opener = dock.takeReturnFocusTarget();
      if (opener?.isConnected) {
        opener.focus();
        return;
      }
      document.querySelector<HTMLElement>('[data-assistant-launcher]')?.focus();
    }, [
      dock,
      isOpen
    ]);

    function closePanel() {
      dock.close();
      // A decision the user walked away from is no longer the one to land on. Left set, it
      // would pull focus onto a card from the previous session the next time the panel opens,
      // instead of into the composer the user just asked for.
      setDecisionToFocus(undefined);
    }

    /**
     * Escape closes the panel.
     *
     * Tab is not held inside it. The panel used to float over the page and kept Tab in, because a
     * keyboard user who tabbed out of it landed somewhere they could not see. Docked, it sits beside
     * the page (#6128), and moving between the two is the point of docking it.
     */
    function handlePanelKeyDown(event: React.KeyboardEvent<HTMLElement>) {
      if (event.key !== KeyboardKey.Escape) return;

      // The panel is what the Escape was aimed at. Left to bubble, it would also reach a drawer or
      // a dialog on the page.
      event.stopPropagation();
      closePanel();
    }

    async function handleSendMessage() {
      const trimmedMessage = inputText.trim();
      if (!trimmedMessage) {
        console.warn('Message cannot be empty');
        return;
      }
      if (trimmedMessage.length > MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS) return;
      if (attachments.sendBlockedReason !== undefined) return;

      // Taken now: the strip is emptied only once the turn is answered, and it is the files on
      // screen at the moment of sending that this message carries.
      const sentAttachments = attachments.uploaded;
      const confirmedActionIds = confirmedActionIdsRef.current;
      // The transcript this turn belongs to. See `transcriptGenerationRef`.
      const generation = transcriptGenerationRef.current;
      // Listened for before the request leaves, so the turn's first step cannot beat it here.
      const turnId = Guid.generate().value;
      setLiveTurnId(turnId);
      liveStep.watch(turnId);

      try {
        setRespondingDelayElapsed(false);
        setDotCount(1);
        setIsSending(true);
        setInputText('');
        setStoppedNotice(undefined);
        setTurnFailure(undefined);
        // Whatever the last turn staged, this one is not it. Cleared before the request rather
        // than after it, so a turn that stages nothing leaves the keyboard in the composer.
        setDecisionToFocus(undefined);

        setMessages(prev => [
          ...prev,
          AssistantResponse.create({
            conversationId: undefined,
            role: Role.User,
            text: trimmedMessage,
            citations: [],
            timestamp: new AHDate(),
            attachments: sentAttachments,
          })
        ]);

        // Held rather than awaited inline, because the handle is what `abort()` lives on and
        // the promise alone cannot be stopped.
        const turn = sendAssistantMessage({
          message: trimmedMessage,
          conversationId,
          currentRoute: location.pathname,
          screenContext: readScreenContext(),
          attachmentIds: sentAttachments.map(attachment => attachment.id.value),
          turnId,
        });
        pendingTurnRef.current = turn;

        const assistantResponse = await turn.unwrap();
        // Sent, so they belong to that conversation now. A failure or a conflict also arrives at
        // 200 but records no turn, so its files were never sent and stay in the composer for the
        // next try, as a stopped turn's do (#6504 review).
        if (assistantResponse.showsAnswerText) attachments.clear();

        // A reply for a transcript the user has since left is saved in its own conversation,
        // where the list finds it, and is not written into whatever is on screen now.
        if (transcriptGenerationRef.current === generation) {
          setConversationId(assistantResponse.conversationId);
          setMessages(prev => appendTurn(prev, assistantResponse, confirmedActionIds));
          setDecisionToFocus(decisionStagedBy(assistantResponse));
        }
      } catch (error) {
        if (wasStoppedByUser(error)) {
          // Said rather than left silent. The user's own message stays where they typed it,
          // and without a word beside it a stopped turn looks like one that is still coming.
          setStoppedNotice('You stopped this response.');
        } else {
          console.error('Error in AI Assistant API Service:', error);
          const attachmentRefusal = turnAttachmentRefusal(error);
          setTurnFailure(attachmentRefusal ?? describeTurnFailure(error));
          // The files were the problem, so the strip says which and offers Retry, rather than
          // showing them attached for a resend that would be refused again.
          if (attachmentRefusal !== undefined) {
            attachments.reject(sentAttachments.map(attachment => attachment.id.value), attachmentRefusal);
          }
        }
      }

      pendingTurnRef.current = undefined;

      // Ran as a `finally` clause until the React Compiler, which cannot lower one, started skipping
      // this component. Nothing follows the try statement, so running it here is equivalent.
      setIsSending(false);
      setRespondingDelayElapsed(false);
      // Answered, failed or stopped: any step still on its way is for a turn that is over.
      liveStep.unwatch(turnId);
      setLiveTurnId(undefined);
    }

    /**
     * Stops the turn in flight.
     *
     * Aborting the request is what stops the work, not just the waiting: the browser drops the
     * connection, ASP.NET Core fires `HttpContext.RequestAborted`, and the token that reaches
     * the retrieval call, the tool loop and the citation judge is that one. The turn is
     * recorded in a single save at the end, so a stopped one records nothing rather than half
     * of something.
     */
    function handleStopResponding() {
      pendingTurnRef.current?.abort();
    }

    /**
     * Puts a different transcript on screen: an earlier conversation, or none at all.
     *
     * Everything that described the old one goes with it. The stopped notice in particular would
     * otherwise sit under a transcript that does not contain the turn it says was stopped.
     */
    function replaceTranscript(transcript: AssistantResponse[], nextConversationId: string | undefined) {
      transcriptGenerationRef.current += 1;
      setMessages(transcript);
      setConversationId(nextConversationId);
      setStoppedNotice(undefined);
      setTurnFailure(undefined);
      setDecisionToFocus(undefined);
      setResolutionFailures({});
    }

    function handleToggleHistory() {
      setHistoryNotice(undefined);
      setShowingHistory(showing => !showing);
    }

    /** Starts afresh. The conversation that was open stays in the list. */
    function handleNewConversation() {
      setHistoryNotice(undefined);
      setShowingHistory(false);
      replaceTranscript([], undefined);
    }

    async function handleOpenConversation(summary: AssistantConversationSummary) {
      if (summary.conversationId === conversationId) {
        setShowingHistory(false);
        return;
      }

      // Counted now rather than when the read answers, so a second choice made while this read
      // is in flight - or "New conversation" - outranks it.
      transcriptGenerationRef.current += 1;
      const generation = transcriptGenerationRef.current;
      setHistoryNotice(undefined);
      setHistoryBusy(true);
      await openEarlierConversation(
        summary.conversationId,
        readConversation,
        () => transcriptGenerationRef.current === generation,
        conversation => {
          replaceTranscript(conversation.toTranscript(), conversation.conversationId);
          setShowingHistory(false);
        },
        setHistoryNotice
      );
      setHistoryBusy(false);
    }

    async function handleDeleteConversation(summary: AssistantConversationSummary) {
      const response = await confirm({
        title: 'Delete this conversation?',
        message:
          `"${summary.opening}" will be deleted, along with the excerpts the assistant quoted in `
          + 'it. This cannot be undone.',
        okButtonText: 'Delete',
      });

      if (response !== ConfirmResponse.Ok) return;

      // Whether it is the one on screen is decided now, against the transcript as it stands.
      const wasOpen = summary.conversationId === conversationId;
      const generation = transcriptGenerationRef.current;
      setHistoryNotice(undefined);
      setHistoryBusy(true);
      await deleteEarlierConversation(
        summary.conversationId,
        deleteConversation,
        () => {
          if (wasOpen && transcriptGenerationRef.current === generation) replaceTranscript([], undefined);
        },
        setHistoryNotice
      );
      setHistoryBusy(false);
    }

    async function handleClearHistory() {
      const response = await confirm({
        title: 'Delete your assistant conversations?',
        message:
          'Every conversation you have had with the assistant will be deleted, along with the '
          + 'excerpts it quoted. This cannot be undone.',
        okButtonText: 'Delete',
      });

      if (response !== ConfirmResponse.Ok) return;

      setHistoryNotice(undefined);
      setHistoryBusy(true);
      await clearAssistantHistory(
        deleteAllConversations,
        () => replaceTranscript([], undefined),
        setHistoryNotice
      );
      setHistoryBusy(false);
    }

    function beginResolving(stagedActionId: string) {
      setResolvingActionIds(previous => new Set(previous).add(stagedActionId));
      // Whatever the last attempt said about this card is stale the moment another starts, and
      // leaving it beside a spinner reads as the new attempt having failed as well.
      setResolutionFailures(previous => withoutResolutionFailure(previous, stagedActionId));
    }

    function handleConfirmStagedAction(stagedActionId: string) {
      // Before the request, not after it: a confirmation whose answer never arrives is exactly
      // the one whose card must not be relabelled by a later turn.
      confirmedActionIdsRef.current.add(stagedActionId);
      beginResolving(stagedActionId);
      void resolveStagedAction(
        confirmStagedAction,
        stagedActionId,
        StagedActionIntent.Confirm,
        setMessages,
        setResolvingActionIds,
        setResolutionFailures
      );
    }

    function handleCancelStagedAction(stagedActionId: string) {
      beginResolving(stagedActionId);
      void resolveStagedAction(
        cancelStagedAction,
        stagedActionId,
        StagedActionIntent.Cancel,
        setMessages,
        setResolvingActionIds,
        setResolutionFailures
      );
    }

    if (!isOpen) return null;

    return (
      // A complementary landmark, named by its title: docked beside the page rather than
      // over it, it is a region of the app and not a dialog. `tabIndex={-1}` so focus can be
      // sent here without putting the panel itself in the tab order.
      <PanelContainer
        id={ASSISTANT_PANEL_ID}
        aria-labelledby={panelTitleId}
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
      >
        <Header>
          {/*
            On the left, apart from the others: on a phone the title is centred in
            about 340 pixels, and three controls on one side reach into it.
            Disabled while a turn is in flight, so its reply cannot land in a
            conversation the user switched to while it ran.
          */}
          <HeaderLeadingActions>
            <Tooltip title={showingHistory ? 'Back to the conversation' : 'Your conversations'}>
              <span>
                <HeaderIconButton
                  aria-label={showingHistory ? 'Back to the conversation' : 'Show your conversations'}
                  disabled={isSending}
                  onClick={handleToggleHistory}
                  size="medium"
                >
                  {showingHistory ? <ArrowBackIcon fontSize='medium' /> : <HistoryIcon fontSize='medium' />}
                </HeaderIconButton>
              </span>
            </Tooltip>
          </HeaderLeadingActions>
          <h3 id={panelTitleId}>
            <AssistantIcon fontSize="small" />
            Assistant
          </h3>
          <HeaderActions>
            <Tooltip title="New conversation">
              <span>
                <HeaderIconButton
                  aria-label="Start a new conversation"
                  disabled={isSending}
                  onClick={handleNewConversation}
                  size="medium"
                >
                  <AddCommentIcon fontSize='medium' />
                </HeaderIconButton>
              </span>
            </Tooltip>
            <Tooltip title="Close chat">
              <HeaderIconButton
                aria-label="Close chat"
                onClick={closePanel}
                size="medium"
              >
                <CloseIcon fontSize='medium' />
              </HeaderIconButton>
            </Tooltip>
          </HeaderActions>
        </Header>
        <LiveAnnouncement
          role="log"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcement}
        </LiveAnnouncement>
        <Content>
          {showingHistory ? (
            <AssistantConversationList
              conversations={conversationSummaries}
              failedToLoad={conversationListFailed}
              openConversationId={conversationId}
              notice={historyNotice}
              busy={historyBusy}
              onOpen={summary => { void handleOpenConversation(summary); }}
              onDelete={summary => { void handleDeleteConversation(summary); }}
              onDeleteAll={() => { void handleClearHistory(); }}
            />
          ) : (
          <>
          <Message sent={false}>
            <MessageIcon>
              <AssistantIcon fontSize="large" />
            </MessageIcon>
            <MessageText className="message-text">
              {session.user?.name.firstName ? `${new AHDate().TimeOfDayGreeting}, ${session.user?.name.firstName}! ` : "Hello! "}
              How can I help?
            </MessageText>
          </Message>
          {messages.map((response: AssistantResponse, messageIndex: number) => (
            <Message
              sent={response.role === Role.User}
              key={`${response.timestamp.toISOString()}-${messageIndex}`}
            >
              {/*
                No slot at all for the user's own turn: an empty one still takes the row's
                gap, and the bubble then stops short of the right edge (#7236).
              */}
              {response.role !== Role.User && (
                <MessageIcon>
                  <AssistantIcon fontSize="large" />
                </MessageIcon>
              )}
              {response.showsAnswerText ? (
                <MessageText className="message-text">
                  <MarkdownStyles>
                    <CitedAnswer
                      text={response.text}
                      citations={response.citations}
                      grounding={response.grounding}
                    />
                  </MarkdownStyles>
                  <SentAttachmentList attachments={response.attachments} />
                  {response.isIncompleteAnswer && (
                    <IncompleteNotice role="status">
                      <ErrorOutlineIcon fontSize="small" />
                      <span>
                        The assistant ran out of steps before finishing this.
                        Treat it as partial, and try asking for one piece at a time.
                      </span>
                    </IncompleteNotice>
                  )}
                  {/*
                    Deliberately not the wording above. That turn met the model's own
                    limit and a narrower question genuinely helps; this one met a
                    limit the server sets, which no rephrasing gets past - so telling
                    the user to try smaller pieces would send them round the same loop
                    to the same place.
                  */}
                  {response.isExhaustedAnswer && (
                    <IncompleteNotice role="status">
                      <ErrorOutlineIcon fontSize="small" />
                      <span>
                        This ran past the work the assistant is allowed to do in one
                        go, so what is above is partial. Asking about a narrower part
                        of it will get further.
                      </span>
                    </IncompleteNotice>
                  )}
                  {response.resultTables.map((table, tableIndex) => (
                    <ResultTableCard key={`${tableIndex}-${table.title}`} table={table} />
                  ))}
                  {response.actionableStagedActions.map(action => (
                    <StagedActionCard
                      key={action.id.value}
                      action={action}
                      /*
                        Only while it is still a decision. Once it is answered the
                        card stops being the place to stand, and focus belongs back
                        in the composer.
                      */
                      ref={
                        action.isPending && action.id.value === decisionToFocus
                          ? captureDecisionCard
                          : undefined
                      }
                      busy={resolvingActionIds.has(action.id.value)}
                      failure={resolutionFailures[action.id.value]}
                      onConfirm={handleConfirmStagedAction}
                      onCancel={handleCancelStagedAction}
                    />
                  ))}
                  {response.sourceLost && (
                    /*
                      Beside the answer, as a withheld excerpt explains itself beside its
                      marker. The answer stays because the reader was shown it; what changed
                      is that the assistant will not read it again (#5211).
                    */
                    <ReplayedActionNotice role="status">
                      <LockIcon fontSize="small" />
                      <span>
                        A source this answer cited is no longer available to you. The answer
                        is shown as you saw it, but the assistant no longer uses it to answer
                        follow-up questions.
                      </span>
                    </ReplayedActionNotice>
                  )}
                  {response.resultTablesNotReplayed && (
                    /*
                      The answer may say "the table below". A table is not kept with the
                      conversation - it was a reading of the records at that moment - so say
                      why there is nothing below it rather than leave the reader looking (#6223).
                    */
                    <ReplayedActionNotice role="status">
                      <HistoryIcon fontSize="small" />
                      <span>
                        This answer was shown with a table that is not kept in your history.
                        Ask again to see the figures as they are now.
                      </span>
                    </ReplayedActionNotice>
                  )}
                  {response.preparedActionsNotReplayed && (
                    <ReplayedActionNotice role="status">
                      <HistoryIcon fontSize="small" />
                      <span>
                        This conversation is being shown from your history. Anything
                        the assistant prepared in it is no longer offered here - ask
                        again to prepare it afresh.
                      </span>
                    </ReplayedActionNotice>
                  )}
                  <TurnStepsSummary steps={response.steps} />
                  {response.isReportable
                    && response.conversationId !== undefined
                    && response.traceId !== undefined && (
                    <AnswerFeedbackControl
                      conversationId={response.conversationId}
                      traceId={response.traceId}
                      feedback={response.feedback}
                    />
                  )}
                  <Timestamp>
                    {response.timestamp.format('h:mm A')}
                  </Timestamp>
                </MessageText>
              ) : (
                <FailedMessageText className="message-text" role="alert">
                  <ErrorOutlineIcon fontSize="small" />
                  <div>
                    {response.text}
                    <Timestamp>
                      {response.timestamp.format('h:mm A')}
                    </Timestamp>
                  </div>
                </FailedMessageText>
              )}
            </Message>
          ))}
          {stoppedNotice !== undefined && (
            <StoppedNotice role="status">
              <StopCircleIcon fontSize="small" />
              <span>{stoppedNotice}</span>
            </StoppedNotice>
          )}
          {turnFailure !== undefined && (
            <StoppedNotice role="alert">
              <ErrorOutlineIcon fontSize="small" />
              <span>{turnFailure}</span>
            </StoppedNotice>
          )}
          {isSending && showResponding && (
            <Message sent={false}>
              <MessageIcon>
                <AssistantIcon fontSize="large" />
              </MessageIcon>
              <MessageText className="message-text">
                {/* Not a live region: the panel's one announcement already says the assistant is
                    working, and a screen reader that spoke every step would talk over the user. */}
                {`${liveStep.label ?? 'Responding'}${'.'.repeat(dotCount)}`}
              </MessageText>
            </Message>
          )}
          <div ref={bottomRef} />
          </>
          )}
        </Content>
        {/* Hidden with the transcript: a message written over the list would go to a
            conversation the user is not looking at. */}
        {!showingHistory && (
        <AssistantAttachmentStrip
          items={attachments.items}
          notice={attachments.notice}
          sendBlockedReason={isMessageEmpty ? undefined : attachments.sendBlockedReason}
          disabled={isSending}
          onRetry={attachments.retry}
          onRemove={attachments.remove}
        />
        )}
        {!showingHistory && (
        <MessageEditor
          inputRef={handleComposerRef}
          multiline
          maxRows={3}
          variant="outlined"
          placeholder="Type your message..."
          fullWidth
          disabled={isSending}
          value={inputText}
          error={messageValidationError !== undefined}
          helperText={messageValidationError}
          onKeyDown={(event) => {
            if (event.key === KeyboardKey.Enter && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              if (canSendMessage) {
                void handleSendMessage();
              }
            }
          }}
          onChange={(e) => setInputText(e.target.value)}
          slotProps={{
            htmlInput: {
              maxLength: MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS,
            },
            input: {
              autoComplete: "off",
              endAdornment: isSending ? (
                // The spinner used to sit inside a disabled Send button, so the one
                // moment a user most wants a way out was the one moment the only
                // control was inert. It still turns - beside the button rather than
                // in place of it - so the turn reads as running and stoppable at once.
                <SendingControls>
                  <SendingSpinner
                    color="inherit"
                    disableShrink
                    size={20}
                  />
                  <Tooltip title="Stop responding">
                    <IconButton
                      aria-label="Stop responding"
                      edge="end"
                      color="primary"
                      onClick={handleStopResponding}
                    >
                      <StopCircleIcon />
                    </IconButton>
                  </Tooltip>
                </SendingControls>
              ) : (
                <ComposerControls>
                  <AttachFilesButton disabled={attachments.isFull} onFiles={attachments.add} />
                  {/*
                    The tooltip sits on a wrapper, so it can explain a disabled button and does
                    not rename it: the button keeps "Send message" as its name.
                  */}
                  <Tooltip title={attachments.sendBlockedReason ?? ''}>
                    <span>
                      <IconButton
                        aria-label="Send message"
                        edge="end"
                        color="primary"
                        disabled={!canSendMessage}
                        onClick={handleSendMessage}
                      >
                        <SendIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </ComposerControls>
              )
            }
          }}
        />
        )}
      </PanelContainer>
    );
  });

/**
 * The assistant's docked panel, drawn while the assistant is open (#6128).
 *
 * Stays mounted while it is closed, so a conversation is still there when it is opened again; the
 * panel itself is what comes and goes.
 */
function AiAssistant() {
  const session = useSession();
  const sessionKey = `${session.user?.id?.value ?? ''}:${session.user?.legalEntityId?.value ?? ''}`;

  return <AiAssistantSession key={sessionKey} />;
}

export default AiAssistant;
